import React, { useState, useEffect } from 'react';
import { X, Search } from 'lucide-react';
import { Link, useNavigate } from 'react-router-dom';
import productService from '../../services/productService';

const SearchModal = ({ isOpen, onClose }) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [inspirationProducts, setInspirationProducts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [isAnimating, setIsAnimating] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    if (isOpen) {
      setIsAnimating(true);
      // Fetch some products for inspiration when modal opens
      const fetchProducts = async () => {
        try {
          setLoading(true);
          const response = await productService.getHomepageData();
          if (response.success) {
            // Get first 4 products from new arrivals or trending
            const products = response.newArrivals.length > 0 
              ? response.newArrivals.slice(0, 4)
              : response.trendingProducts.slice(0, 4);
            setInspirationProducts(products);
          }
        } catch (error) {
          console.error('Error fetching inspiration products:', error);
        } finally {
          setLoading(false);
        }
      };
      fetchProducts();
      
      // Prevent body scroll when modal is open
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }

    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  const handleSearch = (e) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      navigate(`/search?q=${encodeURIComponent(searchQuery)}`);
      onClose();
      setSearchQuery('');
    }
  };

  const handleClose = () => {
    setIsAnimating(false);
    setTimeout(() => {
      onClose();
    }, 500); // Wait for animation to complete
  };

  if (!isOpen && !isAnimating) return null;

  // Skeleton Loader Component
  const ProductSkeleton = () => (
    <div className="animate-pulse">
      <div className="relative bg-gray-200 rounded-lg overflow-hidden mb-3 h-64"></div>
      <div className="h-4 bg-gray-200 rounded mb-2 w-3/4"></div>
      <div className="h-3 bg-gray-200 rounded w-1/2"></div>
    </div>
  );

  return (
    <>
      {/* Backdrop */}
      <div 
        className={`fixed inset-0 bg-black z-[60] transition-opacity duration-500 ${
          isOpen && isAnimating ? 'opacity-50' : 'opacity-0'
        }`}
        onClick={handleClose}
      />
      
      {/* Modal - Full Screen */}
      <div 
        className={`fixed top-0 left-0 w-full h-full bg-white z-[70] transform transition-transform duration-500 ease-out ${
          isOpen && isAnimating ? 'translate-y-0' : '-translate-y-full'
        }`}
        style={{ overflowY: 'auto' }}
      >
        <div className="max-w-4xl mx-auto px-8 py-12 relative min-h-full">
          {/* Close Button */}
          <button
            onClick={handleClose}
            className="absolute top-8 right-8 p-2 hover:bg-gray-100 rounded-full transition-colors z-10"
            aria-label="Close search"
          >
            <X className="w-6 h-6" />
          </button>

          {/* Search Section */}
          <div className="mb-12">
            <h2 className="text-3xl font-normal text-center mb-8">Search our site</h2>
            
            <form onSubmit={handleSearch} className="relative max-w-2xl mx-auto">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder=""
                  className="w-full px-10 py-2.5 border-2 border-gray-300 rounded-lg text-sm focus:outline-none focus:border-[#6B9FA8] transition-colors"
                  autoFocus
                />
              </div>
            </form>
          </div>

          {/* Inspiration Section */}
          <div>
            <h3 className="text-xl font-normal mb-6">Need some inspiration?</h3>
            
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-6">
              {loading ? (
                // Show 4 skeleton loaders
                <>
                  <ProductSkeleton />
                  <ProductSkeleton />
                  <ProductSkeleton />
                  <ProductSkeleton />
                </>
              ) : (
                inspirationProducts.map((product) => (
                  <Link
                    key={product._id}
                    to={`/product/${product._id}`}
                    onClick={handleClose}
                    className="group"
                  >
                    <div className="relative bg-gray-100 rounded-lg overflow-hidden mb-3">
                      <img
                        src={product.images && product.images[0] ? product.images[0].path : 'https://via.placeholder.com/300'}
                        alt={product.productTitle}
                        className="w-full h-64 object-cover transition-transform duration-300 group-hover:scale-105"
                      />
                      {product.soldOut && (
                        <span className="absolute top-3 right-3 bg-white px-3 py-1 rounded-full text-sm">
                          Sold out
                        </span>
                      )}
                    </div>
                    <h4 className="text-sm font-medium mb-1 line-clamp-2">
                      {product.productTitle}
                    </h4>
                    <p className="text-sm text-gray-600">
                      {product.msrp ? `From Login to View Price` : 'Login to View Price'}
                    </p>
                  </Link>
                ))
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default SearchModal;
